import React, { useState, useEffect } from "react";
import emailjs from "@emailjs/browser";
import {
  FaPaperPlane,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaLinkedin,
  FaGithub,
  FaXing,
} from "react-icons/fa";
import { contactData } from "../data/portfolioData";

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [status, setStatus] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      console.error(
        "EmailJS is not configured. Set REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID, and REACT_APP_EMAILJS_PUBLIC_KEY."
      );
      setStatus("error");
      return;
    }

    setIsSending(true);
    setStatus("");

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      setStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error("Failed to send message:", err);
      setStatus("error");
    } finally {
      setIsSending(false);
      setTimeout(() => setStatus(""), 4000);
    }
  };

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      title: "Email",
      value: contactData.email,
      color: "text-emerald-500",
      link: `mailto:${contactData.email}`,
    },
    {
      icon: <FaPhone />,
      title: "Phone",
      value: contactData.phone,
      color: "text-cyan-500",
      link: `tel:${contactData.phone.replace(/[^0-9+]/g, '')}`,
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Location",
      value: contactData.location,
      color: "text-purple-500",
      link: "#",
    },
  ];

  const socialLinks = [
    {
      icon: <FaGithub />,
      href: contactData.socialLinks.github,
      label: "GitHub",
    },
    {
      icon: <FaLinkedin />,
      href: contactData.socialLinks.linkedin,
      label: "LinkedIn",
    },
    {
      icon: <FaXing />,
      href: contactData.socialLinks.xing,
      label: "Xing",
    },
    {
      icon: <FaEnvelope />,
      href: `mailto:${contactData.email}`,
      label: "Email",
    },
  ];

  return (
    <section id="contact" className="relative min-h-screen flex items-center justify-center py-12 sm:py-16 overflow-hidden">
      {/* Desktop Background */}
      {!isMobile && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900" />
          <div className="absolute inset-0 dark:opacity-5 opacity-10">
            <div className="absolute inset-0 bg-grid-pattern" />
          </div>
          <div className="absolute top-20 -left-32 w-96 h-96 bg-emerald-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float" />
          <div className="absolute bottom-20 -right-32 w-96 h-96 bg-cyan-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float animation-delay-2000" />
        </>
      )}

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 ">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">
            Contact <span className="gradient-text">Me</span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed max-w-2xl mx-auto">
            Have a project in mind? Let's discuss how we can work together
          </p>
        </div>

        {/* ==================== DESKTOP VIEW (Original Layout) ==================== */}
        {!isMobile && (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info - Left Column */}
            <div className="space-y-6">
              <div className="p-6 rounded-2xl bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/50 hover:shadow-xl transition-all duration-300">
                <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-emerald-600 to-cyan-500 bg-clip-text text-transparent">
                  Contact Information
                </h3>
                <div className="space-y-5">
                  {contactInfo.map((info, index) => (
                    <a
                      key={index}
                      href={info.link}
                      className="flex items-start gap-4 group p-2 -m-2 rounded-xl transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:scale-105"
                    >
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center ${info.color} text-xl`}>
                        {info.icon}
                      </div>
                      <div className="flex-1">
                        <div className="text-gray-600 dark:text-gray-400 text-sm">{info.title}</div>
                        <div className="text-gray-900 dark:text-gray-100 text-sm font-medium group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                          {info.value}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/50 hover:shadow-xl transition-all duration-300">
                <h4 className="text-lg font-bold mb-4 bg-gradient-to-r from-emerald-600 to-cyan-500 bg-clip-text text-transparent">
                  Connect With Me
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex flex-col items-center justify-center gap-2 transition-all duration-300 hover:shadow-lg hover:text-emerald-600 dark:hover:text-emerald-400"
                    >
                      <div className="text-lg">{social.icon}</div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{social.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form - Right Column */}
            <div className="lg:col-span-2">
              <div className="p-6 md:p-8 rounded-2xl bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/50 hover:shadow-xl transition-all duration-300">
                <h3 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-emerald-600 to-cyan-500 bg-clip-text text-transparent">
                  Send Me a Message
                </h3>

                {status === "success" && (
                  <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 animate-fadeIn">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg">✓</div>
                      <div>
                        <div className="font-bold text-emerald-600 dark:text-emerald-400 text-sm">Message Sent!</div>
                        <div className="text-gray-600 dark:text-gray-400 text-sm">I'll get back to you within 24 hours.</div>
                      </div>
                    </div>
                  </div>
                )}

                {status === "error" && (
                  <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 animate-fadeIn">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold text-lg">!</div>
                      <div>
                        <div className="font-bold text-red-600 dark:text-red-400 text-sm">Message not sent</div>
                        <div className="text-gray-600 dark:text-gray-400 text-sm">Please try again or email me directly.</div>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-gray-600 dark:text-gray-400 text-sm mb-2">Your Name</label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required
                        className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                        placeholder="John Doe" />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-gray-600 dark:text-gray-400 text-sm mb-2">Your Email</label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                        className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                        placeholder="john@example.com" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-gray-600 dark:text-gray-400 text-sm mb-2">Subject</label>
                    <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required
                      className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                      placeholder="Project Inquiry" />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-gray-600 dark:text-gray-400 text-sm mb-2">Your Message</label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows="5"
                      className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 resize-none"
                      placeholder="Tell me about your project..." />
                  </div>

                  <button type="submit" disabled={isSending} className="w-full group relative px-6 py-3 bg-gradient-to-r from-emerald-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-300 hover:scale-[1.02] overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100">
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                    <div className="relative flex items-center justify-center gap-2">
                      <FaPaperPlane className="text-sm" />
                      <span>{isSending ? "Sending..." : "Send Message"}</span>
                    </div>
                  </button>
                </form>

                <div className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-gray-600 dark:text-gray-400 text-sm">Prefer email? Drop me a line at</p>
                  <a href={`mailto:${contactData.email}`} className="text-emerald-600 dark:text-emerald-400 font-semibold hover:underline">
                    {contactData.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== MOBILE VIEW (Simple Stacked Layout) ==================== */}
        {isMobile && (
          <div className="space-y-5">
            {/* 1. Contact Information - FIRST */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h3>
              <div className="space-y-3">
                {contactInfo.map((info, index) => (
                  <a key={index} href={info.link} className="flex items-center gap-3 group">
                    <div className={`${info.color}`}>{info.icon}</div>
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{info.title}</div>
                      <div className="text-sm text-gray-900 dark:text-gray-100 group-hover:text-emerald-600 transition-colors">
                        {info.value}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* 2. Send Me a Message Form - SECOND */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Send Me a Message</h3>

              {status === "success" && (
                <div className="mb-4 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">✓</div>
                    <div>
                      <div className="font-medium text-emerald-600 dark:text-emerald-400 text-sm">Message Sent!</div>
                      <div className="text-gray-600 dark:text-gray-400 text-sm">I'll get back to you within 24 hours.</div>
                    </div>
                  </div>
                </div>
              )}

              {status === "error" && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">!</div>
                    <div>
                      <div className="font-medium text-red-600 dark:text-red-400 text-sm">Message not sent</div>
                      <div className="text-gray-600 dark:text-gray-400 text-sm">Please try again or email me directly.</div>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name-mobile" className="block text-gray-700 dark:text-gray-300 text-sm mb-1">Your Name</label>
                  <input type="text" id="name-mobile" name="name" value={formData.name} onChange={handleChange} required
                    className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    placeholder="John Doe" />
                </div>

                <div>
                  <label htmlFor="email-mobile" className="block text-gray-700 dark:text-gray-300 text-sm mb-1">Your Email</label>
                  <input type="email" id="email-mobile" name="email" value={formData.email} onChange={handleChange} required
                    className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    placeholder="john@example.com" />
                </div>

                <div>
                  <label htmlFor="subject-mobile" className="block text-gray-700 dark:text-gray-300 text-sm mb-1">Subject</label>
                  <input type="text" id="subject-mobile" name="subject" value={formData.subject} onChange={handleChange} required
                    className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    placeholder="Project Inquiry" />
                </div>

                <div>
                  <label htmlFor="message-mobile" className="block text-gray-700 dark:text-gray-300 text-sm mb-1">Your Message</label>
                  <textarea id="message-mobile" name="message" value={formData.message} onChange={handleChange} required rows="4"
                    className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 resize-none"
                    placeholder="Tell me about your project..." />
                </div>

                <button type="submit" disabled={isSending} className="w-full px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-cyan-500 text-white font-medium rounded-lg transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                  <FaPaperPlane className="text-sm" />
                  <span>{isSending ? "Sending..." : "Send Message"}</span>
                </button>
              </form>

              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Prefer email? Drop me a line at
                </p>
                <a href={`mailto:${contactData.email}`} className="text-emerald-600 dark:text-emerald-400 font-medium hover:underline text-sm">
                  {contactData.email}
                </a>
              </div>
            </div>

            {/* 3. Connect With Me - THIRD (UNDER the form) */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Connect With Me</h3>
              <div className="flex flex-wrap gap-2">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm hover:bg-emerald-50 dark:hover:bg-emerald-500/10 hover:text-emerald-600 transition-all duration-200"
                  >
                    {social.icon}
                    <span>{social.label}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .bg-grid-pattern {
          background-image:
            linear-gradient(to right, #d1d5db 1px, transparent 1px),
            linear-gradient(to bottom, #d1d5db 1px, transparent 1px);
          background-size: 40px 40px;
        }
        .dark .bg-grid-pattern {
          background-image:
            linear-gradient(to right, #374151 1px, transparent 1px),
            linear-gradient(to bottom, #374151 1px, transparent 1px);
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(15px, -15px) scale(1.05); }
        }
        .animate-float { animation: float 8s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: 2s; }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }

        .gradient-text {
          background: linear-gradient(135deg, #10b981, #06b6d4);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
      `}</style>
    </section>
  );
};

export default Contact;